import { useEffect, useState } from "react";
import { getRestOutbox } from "./restOutbox";

/**
 * Reactive view of the REST-replay outbox for the UI's "queued / will sync"
 * affordance. Returns the set of unsynced mutation keys (`reaction:<cid>:<emoji>`,
 * `edit:<cid>`, `create:<tempId>`) and the composite online state. The set is
 * refreshed on every queue progress transition (enqueue, drain, confirm) so a chip
 * shows a pending dot the instant it is queued and clears it the instant it syncs.
 */
export function useRestOutbox(): { online: boolean; pending: Set<string> } {
  const rest = getRestOutbox();
  const [pending, setPending] = useState<Set<string>>(() => new Set());
  const [online, setOnline] = useState<boolean>(() => rest.isOnline);

  useEffect(() => {
    let alive = true;
    const refresh = (): void => {
      setOnline(rest.isOnline);
      void rest.pendingKeys().then((s) => {
        if (alive) setPending(s);
      });
    };
    refresh();
    const offProgress = rest.onProgress(refresh);
    // `online`/`offline` are only a trigger — the detector's heartbeat is the
    // authority — so re-read `isOnline` shortly after the event settles the probe.
    const onEvt = (): void => {
      setOnline(rest.isOnline);
      setTimeout(refresh, 60);
    };
    window.addEventListener("online", onEvt);
    window.addEventListener("offline", onEvt);
    return () => {
      alive = false;
      offProgress();
      window.removeEventListener("online", onEvt);
      window.removeEventListener("offline", onEvt);
    };
  }, [rest]);

  return { online, pending };
}
